import { createHash } from 'node:crypto';
import { Command } from 'commander';
import ora, { type Ora } from 'ora';
import {
  getCrawlStrategy,
  IntelligentCrawler,
  type CrawlProgress,
} from '../../crawl/intelligent-crawler.js';
import { ChunkingService } from '../../services/chunking.service.js';
import { createServices, destroyServices } from '../../services/index.js';
import { classifyWebContentType } from '../../services/index.service.js';
import { createDocumentId } from '../../types/brands.js';
import type { Document } from '../../types/document.js';
import type { WebStore } from '../../types/store.js';
import type { GlobalOptions } from '../program.js';

export function createCrawlCommand(getOptions: () => GlobalOptions): Command {
  return new Command('crawl')
    .description('Crawl web pages with natural language control and index into store')
    .argument('<url>', 'URL to crawl')
    .argument('<store>', 'Target web store to add crawled content to')
    .option(
      '--crawl <instruction>',
      'Natural language instruction for what to crawl (e.g., "all Getting Started pages")'
    )
    .option(
      '--extract <instruction>',
      'Natural language instruction for what to extract (e.g., "extract API references")'
    )
    .option('--max-pages <number>', 'Maximum number of pages to crawl', '50')
    .option('--fast', 'Use fast axios-only mode (may fail on JavaScript-heavy sites)')
    .allowUnknownOption() // Allow --store= to be parsed so we can show helpful error
    .action(
      async (
        url: string,
        storeIdOrName: string,
        cmdOptions: {
          crawl?: string;
          extract?: string;
          maxPages: string;
          fast?: boolean;
        }
      ) => {
        // Detect common mistake: using --store= instead of positional argument
        const storeArg = process.argv.find((arg) => arg.startsWith('--store'));
        if (storeArg !== undefined) {
          const storeName = storeArg.includes('=') ? storeArg.split('=')[1] : 'NAME';
          const storeNameDisplay =
            storeName !== undefined && storeName !== '' ? storeName : 'my-store';
          console.error(`
Error: Store name should be a positional argument, not an option.

  WRONG: crawl ${url} ${storeArg}
  RIGHT: crawl ${url} ${storeNameDisplay}

Example: bluera-knowledge crawl https://docs.example.com my-docs
`);
          process.exitCode = 1;
          return;
        }

        const globalOpts = getOptions();
        const useHeadless = !(cmdOptions.fast ?? false);

        // IMPORTANT: Get crawl strategy BEFORE initializing lancedb services.
        // LanceDB's native Rust code is not fork-safe. Spawning Claude CLI after
        // lancedb is loaded corrupts the mutex state, causing crashes.
        const crawlInstruction = cmdOptions.crawl ?? 'crawl all pages linked from this URL';

        if (globalOpts.quiet !== true && globalOpts.format !== 'json') {
          console.log(`Crawling ${url}`);
          console.log('Analyzing page structure with Claude...');
        }
        const preComputedStrategy = await getCrawlStrategy(url, crawlInstruction, useHeadless);
        if (globalOpts.quiet !== true && globalOpts.format !== 'json') {
          console.log(
            `Claude identified ${String(preComputedStrategy.urls.length)} URLs: ${preComputedStrategy.reasoning}`
          );
        }

        // Now safe to initialize lancedb services
        const services = await createServices(
          globalOpts.config,
          globalOpts.dataDir,
          globalOpts.projectRoot
        );

        // Look up or auto-create web store
        let store: WebStore;
        let storeCreated = false;
        const existingStore = await services.store.getByIdOrName(storeIdOrName);

        if (!existingStore) {
          // Auto-create web store
          const result = await services.store.create({
            name: storeIdOrName,
            type: 'web',
            url,
          });
          if (!result.success) {
            await destroyServices(services);
            throw new Error(`Failed to create store: ${result.error.message}`);
          }
          // Type narrowing: success check above ensures result.data is Store
          // We know it's a WebStore because we created it with type: 'web'
          const createdStore = result.data;
          if (createdStore.type !== 'web') {
            throw new Error('Unexpected store type after creation');
          }
          store = createdStore;
          storeCreated = true;
          if (globalOpts.quiet !== true && globalOpts.format !== 'json') {
            console.log(`Created web store: ${store.name}`);
          }
        } else if (existingStore.type !== 'web') {
          await destroyServices(services);
          throw new Error(
            `Store "${storeIdOrName}" exists but is not a web store (type: ${existingStore.type})`
          );
        } else {
          store = existingStore;
        }

        const maxPages = parseInt(cmdOptions.maxPages, 10);
        if (Number.isNaN(maxPages)) {
          throw new Error(
            `Invalid value for --max-pages: "${cmdOptions.maxPages}" is not a valid integer`
          );
        }

        // Use spinner in interactive mode
        const isInteractive =
          process.stdout.isTTY && globalOpts.quiet !== true && globalOpts.format !== 'json';
        let spinner: Ora | undefined;

        if (isInteractive) {
          spinner = ora(`Crawling ${url} (intelligent mode)`).start();
        } else if (globalOpts.quiet !== true && globalOpts.format !== 'json') {
          console.log(`Crawling ${url}`);
        }

        const appConfig = await services.config.load();
        const crawler = new IntelligentCrawler(appConfig.crawl);
        // Use web preset for larger prose-friendly chunks
        const webChunker = ChunkingService.forContentType('web');
        let pagesIndexed = 0;
        let chunksCreated = 0;
        let exitCode = 0;

        // Listen for progress events
        crawler.on('progress', (progress: CrawlProgress) => {
          if (spinner) {
            if (progress.type === 'strategy') {
              spinner.text = progress.message ?? 'Analyzing crawl strategy...';
            } else if (progress.type === 'page') {
              const url = progress.currentUrl ?? 'unknown';
              spinner.text = `Crawling ${String(progress.pagesVisited + 1)}/${String(maxPages)} - ${url}`;
            } else if (progress.type === 'extraction') {
              const url = progress.currentUrl ?? 'unknown';
              spinner.text = `Extracting from ${url}...`;
            } else if (progress.type === 'error' && progress.message !== undefined) {
              spinner.warn(progress.message);
            }
          }
        });

        try {
          services.lance.setDimensions(await services.embeddings.ensureDimensions());
          await services.lance.initialize(store.id);
          const docs: Document[] = [];

          // Crawl pages using IntelligentCrawler
          for await (const result of crawler.crawl(url, {
            crawlInstruction,
            ...(cmdOptions.extract !== undefined && { extractInstruction: cmdOptions.extract }),
            maxPages,
            useHeadless,
            preComputedStrategy,
          })) {
            // Use extracted content if available, otherwise markdown
            const contentToProcess = result.extracted ?? result.markdown;

            // Chunk the content using markdown-aware chunking (web content is converted to markdown)
            const chunks = webChunker.chunk(contentToProcess, `${result.url}.md`);
            const fileType = classifyWebContentType(result.url, result.title);
            const urlHash = createHash('md5').update(result.url).digest('hex');

            for (const chunk of chunks) {
              const chunkId =
                chunks.length > 1
                  ? `${store.id}-${urlHash}-${String(chunk.chunkIndex)}`
                  : `${store.id}-${urlHash}`;
              const vector = await services.embeddings.embedDocument(chunk.content);

              docs.push({
                id: createDocumentId(chunkId),
                content: chunk.content,
                vector,
                metadata: {
                  type: chunks.length > 1 ? 'chunk' : 'web',
                  storeId: store.id,
                  url: result.url,
                  title: result.title,
                  extracted: result.extracted !== undefined,
                  depth: result.depth,
                  indexedAt: new Date().toISOString(),
                  fileType,
                  chunkIndex: chunk.chunkIndex,
                  totalChunks: chunk.totalChunks,
                  sectionHeader: chunk.sectionHeader,
                },
              });
              chunksCreated++;
            }

            pagesIndexed++;
          }

          // Index all documents
          if (docs.length > 0) {
            if (spinner) {
              spinner.text = 'Indexing documents...';
            }
            // Clear existing documents to prevent duplicates on re-crawl
            await services.lance.clearAllDocuments(store.id);
            await services.lance.addDocuments(store.id, docs);
            // Create FTS index for full-text search
            await services.lance.createFtsIndex(store.id);
          }

          const crawlResult = {
            success: true,
            store: store.name,
            storeCreated,
            url,
            pagesCrawled: pagesIndexed,
            chunksCreated,
            mode: 'intelligent',
            hadCrawlInstruction: cmdOptions.crawl !== undefined,
            hadExtractInstruction: cmdOptions.extract !== undefined,
          };

          if (globalOpts.format === 'json') {
            console.log(JSON.stringify(crawlResult, null, 2));
          } else if (spinner !== undefined) {
            spinner.succeed(
              `Crawled ${String(pagesIndexed)} pages, indexed ${String(chunksCreated)} chunks`
            );
          } else if (globalOpts.quiet !== true) {
            console.log(
              `Crawled ${String(pagesIndexed)} pages, indexed ${String(chunksCreated)} chunks`
            );
          }
        } catch (error) {
          const message = `Crawl failed: ${error instanceof Error ? error.message : String(error)}`;
          if (spinner) {
            spinner.fail(message);
          } else {
            console.error(`Error: ${message}`);
          }
          exitCode = 6;
        } finally {
          await crawler.stop();
          await destroyServices(services);
        }

        if (exitCode !== 0) {
          // Using process.exitCode avoids mutex crashes from native code (LanceDB, tree-sitter)
          process.exitCode = exitCode;
        }
      }
    );
}
